import type React from 'react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Show, ShowFirstMatching } from '@components/Show';
import { useForm } from '@hooks/form';
import { mdiChevronLeft } from '@mdi/js';
import type { UIComponentBase } from '@root/@types/types';
import { resetPassword } from 'aws-amplify/auth';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';

import Button from '../../components/Button';
import Form from '../../components/Form';
import H3 from '../../components/H3';
import IconMdi from '../../components/IconMdi';
import InputText from '../../components/InputText';

import AuthLayout from './AuthLayout';

const InfoText: React.FC<UIComponentBase> = ({ children, className }) => (
  <p className={twMerge('text-info-700 mb-6 text-sm', className)}>{children}</p>
);

const ForgotPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [areInstructionsSent, setInstructionsSent] = useState<boolean | never>();
  const { getFieldProps, isValid, isLoading, onSubmit, formError, formValues } = useForm<{ email: string }>({
    initialState: { email: location.state?.email ?? '' },
    onSubmit: async (formValues) => {
      // Whether or not the address belongs to an account, the outcome shown is identical —
      // surfacing Cognito's error would tell an anonymous visitor which emails are registered.
      try {
        await resetPassword({ username: formValues.email.trim() });
      } catch {
        // no-op
      }

      setInstructionsSent(true);
    },
    validationSchema: yup.object().shape({
      email: yup.string().required(),
    }),
  });

  const handleSubmit = async () => {
    await onSubmit();
  };

  return (
    <AuthLayout>
      <ShowFirstMatching>
        <Show when={areInstructionsSent}>
          <div className="flex flex-col gap-3">
            <div>
              <H3 className="mb-2 font-['Inter'] font-semibold!">Instructions sent</H3>
              <InfoText>If an account exists for this email, we've sent password reset instructions.</InfoText>
              <InfoText>Please check your inbox.</InfoText>
            </div>
            <Button
              kind="primary"
              className="h-10 w-full"
              onClick={() => navigate('/login', { state: { email: formValues?.email } })}
            >
              Return to sign in
            </Button>
          </div>
        </Show>
        <Show when={!areInstructionsSent}>
          <H3 className="mb-2 font-['Inter'] font-semibold!">Forgotten your password?</H3>
          <InfoText>Confirm your email address and we'll send you instructions to reset your password.</InfoText>
          <Form onSubmit={handleSubmit}>
            <InputText
              errors={formError}
              isPublic={true}
              className="mb-6"
              labelText="Confirm your email address"
              type="email"
              {...getFieldProps('email')}
            />
            <Button kind="primary" type="submit" isDisabled={!isValid || isLoading} className="h-10 w-full">
              Send reset instructions
            </Button>
            <Link
              to={'/login'}
              state={{ email: formValues?.email }}
              className="cb--link text-info-500 mt-3 flex w-fit items-center gap-1 text-sm font-medium"
            >
              <IconMdi path={mdiChevronLeft} size={0.7} />
              Return to Sign in
            </Link>
          </Form>
        </Show>
      </ShowFirstMatching>
    </AuthLayout>
  );
};

export default ForgotPassword;
